import { StateCreator } from 'zustand'
import { logger, enqueueLoadedSnackbar, exceptionMessageHandler } from '$utils'
import { SharedSlice } from '../../../../../slice'
import { LeaveType_Old } from '$models'
import { leaveTypeService } from '$services'

interface EditLeaveTypeState {
  leaveType: LeaveType_Old | null
}

export interface EditLeaveTypeSlice extends EditLeaveTypeState {
  /**
   * Reset LeaveType Slice
   */
  resetEditLeaveTypeSlice: () => void

  /**
   * Get LeaveType By id
   * @param leaveTypeId LeaveType id
   */
  getLeaveType: (leaveTypeId: string) => Promise<void>

  /**
   * Update LeaveType
   * @param leaveType LeaveType to update
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   * @returns void
   */
  updateLeaveType: (
    leaveType: LeaveType_Old,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>

  /**
   * Enable LeaveType
   * @param leaveType LeaveType to enable
   * @param successMessage Success Message to display if the enable was successful
   * @param errorMessage Error Message to display if the enable has failed
   * @returns void
   */
  enableLeaveType: (
    leaveType: LeaveType_Old,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>

  /**
   * Delete LeaveType
   * @param leaveTypeId LeaveTypeId to delete
   * @param successMessage Success Message to display if the delete was successful
   * @param errorMessage Error Message to display if the delete has failed
   * @returns boolean
   */
  removeLeaveType: (
    leaveTypeId: string,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<boolean>
}

const initialState: EditLeaveTypeState = {
  leaveType: null,
}

const createEditLeaveTypeSlice: StateCreator<
  EditLeaveTypeSlice & SharedSlice,
  [],
  [],
  EditLeaveTypeSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State setters
  resetEditLeaveTypeSlice: () => set(initialState),

  // State API calls
  getLeaveType: async (leaveTypeId: string): Promise<void> => {
    return leaveTypeService
      .getById(leaveTypeId)
      .then((result) =>
        set({
          leaveType: result,
        })
      )
      .catch((error) => {
        logger.error(`Failed to retrieve leave type ${leaveTypeId}`, error)
        set({
          leaveType: null,
        })

        exceptionMessageHandler(error, 'Failed to retrieve leave type')
      })
  },

  updateLeaveType: async (
    leaveType: LeaveType_Old,
    successMessage?: string,
    errorMessage?: string
  ) => {
    try {
      set(() => ({ isLoading: true }))

      const updatedLeaveType: LeaveType_Old = await leaveTypeService.update(
        leaveType
      )

      set(() => ({
        leaveType: updatedLeaveType,
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
      if (!updatedLeaveType) {
        logger.error(`${errorMessage} - updatedLeaveType doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    }
  },

  enableLeaveType: async (
    leaveType: LeaveType_Old,
    successMessage?: string,
    errorMessage?: string
  ) => {
    try {
      set(() => ({ isLoading: true }))

      const updatedLeaveType: LeaveType_Old = await leaveTypeService.enable(
        leaveType
      )

      set(() => ({
        leaveType: updatedLeaveType,
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
      if (!updatedLeaveType) {
        logger.error(`${errorMessage} - updatedLeaveType doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    }
  },

  removeLeaveType: async (
    leaveTypeId: string,
    successMessage?: string,
    errorMessage?: string
  ) => {
    try {
      set(() => ({ isLoading: true }))

      await leaveTypeService.remove(leaveTypeId)

      enqueueLoadedSnackbar(successMessage, 'success')
      return true
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
      return false
    }
  },
})

export default createEditLeaveTypeSlice
